import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import {
  Button,
  CheckBox,
  FormGroup,
  Input,
  ToggleButton,
  color,
  spacing,
  Typography
} from '@uniquegood/realworld-studio-design';
import { Controller, useForm } from 'react-hook-form';
import { MemoryRouter, Route, useParams } from 'react-router-dom';
import { KeyedMutator } from 'swr';

import { coreApi } from '@/api';
import { useRequest } from '@/hooks';
import { invalidFieldBoilerplate, toast } from '@/utils';
import ActionHandler from '@/components/ActionHandler';

import type { QuestRequestBody } from '@/pages/missions/types';
import {
  buildQuestLabel,
  getQuestButtonLabel,
  isCustomQuestButtonLabel,
  MISSION_API_VERSION_HEADER
} from '@/pages/missions/utils';
import {
  validateCustomButtonLabel,
  validateHiddenQuestReveal
} from '@/pages/missions/questValidation';

interface FormData extends QuestRequestBody {
  isCustomizeButtonText: boolean;
  forceChangeIsVisible?: boolean;
  forceChangeIsLocked?: boolean;
}

interface SidePanelQuestEditProps {
  missionId: number;
  questId?: number;
  mutate: KeyedMutator<Mission[]>;
  onClose: () => void;
}

export default function SidePanelQuestEdit({
  missionId,
  questId,
  mutate,
  onClose
}: SidePanelQuestEditProps) {
  const { appId, projectId, scenarioId } = useParams<AppParam>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: missions } = useRequest<Mission[]>(
    `/apps/${appId}/scenarios/${scenarioId}/missions`,
    {},
    MISSION_API_VERSION_HEADER
  );
  const mission = missions?.find((m) => m.id === missionId);
  const quest = mission?.quests?.find((q) => q.id === questId);

  const { control, handleSubmit, reset, formState, setError, clearErrors } = useForm<FormData>();

  useEffect(() => {
    if (quest) {
      reset({
        name: quest.name,
        description: quest.description,
        label: getQuestButtonLabel(quest),
        isVisibleDefault: quest.isVisibleDefault,
        isLockedDefault: quest.isLockedDefault,
        isCustomizeButtonText: isCustomQuestButtonLabel(quest)
      });
    }
  }, [quest?.id]);

  const onSubmitQuestInfo = handleSubmit(
    async ({ isCustomizeButtonText, forceChangeIsVisible, forceChangeIsLocked, ...rest }) => {
      if (!questId) return;

      clearErrors(['label', 'isVisibleDefault']);

      const customButtonLabelError = validateCustomButtonLabel({
        isCustomizeButtonText,
        label: rest.label
      });
      if (customButtonLabelError) {
        setError(
          'label',
          { type: 'required', message: customButtonLabelError },
          { shouldFocus: true }
        );
        return;
      }

      const hiddenQuestRevealError = validateHiddenQuestReveal({
        missions,
        questId,
        isVisibleDefault: rest.isVisibleDefault
      });
      if (hiddenQuestRevealError) {
        setError('isVisibleDefault', { type: 'validate', message: hiddenQuestRevealError });
        return;
      }

      setIsSubmitting(true);
      try {
        const label = buildQuestLabel({
          name: rest.name,
          label: rest.label,
          isCustomizeButtonText
        });
        await coreApi.put(
          `/apps/${appId}/quests/${questId}`,
          {
            ...rest,
            label,
            forceChangeIsVisible,
            forceChangeIsLocked
          },
          MISSION_API_VERSION_HEADER
        );
        await mutate();
        toast({ message: '퀘스트 정보 저장 완료!' });
      } finally {
        setIsSubmitting(false);
      }
    }
  );

  const handleDeleteQuest = async () => {
    if (!questId) return;
    await coreApi.delete(`/apps/${appId}/quests/${questId}`, MISSION_API_VERSION_HEADER);
    await mutate();
    toast({ message: '퀘스트를 삭제했어요' });
    onClose();
  };

  // Build the fake path for MemoryRouter so ActionHandler's useParams works
  const fakePath = `/apps/${appId}/projects/${projectId}/scenarios/${scenarioId}/missions/${missionId}/quests/${questId}`;

  if (!questId) {
    return (
      <Typography as="p" type="body" textColor="subdued">
        퀘스트를 선택해주세요
      </Typography>
    );
  }

  return (
    <div css={Layout}>
      {/* Quest Info Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmitQuestInfo();
        }}
        css={FormSection}
      >
        <Typography as="h4" type="button" cssStyle={SectionTitle}>
          퀘스트 정보
        </Typography>

        <Controller
          control={control}
          name="name"
          rules={{ required: true }}
          render={({ field: { value, onChange }, fieldState: { invalid } }) => (
            <FormGroup
              label="퀘스트 이름"
              requiredIndicator
              errorText={invalidFieldBoilerplate(invalid)}
            >
              <Input value={value ?? ''} onChange={onChange} />
            </FormGroup>
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({ field: { value, onChange } }) => (
            <FormGroup label="퀘스트 설명">
              <Input multiline value={value ?? ''} onChange={onChange} />
            </FormGroup>
          )}
        />

        <Controller
          control={control}
          name="isCustomizeButtonText"
          render={({ field: { value, onChange } }) => (
            <>
              <CheckBox
                checked={value}
                onChange={(checked) => {
                  clearErrors('label');
                  onChange(checked);
                }}
              >
                퀘스트 버튼 문구를 다르게 설정
              </CheckBox>
              {value && (
                <Controller
                  control={control}
                  name="label"
                  render={({
                    field: { value: labelVal, onChange: labelChange },
                    fieldState: { invalid, error }
                  }) => (
                    <FormGroup
                      label="퀘스트 버튼 문구"
                      errorText={error?.message ?? invalidFieldBoilerplate(invalid)}
                    >
                      <Input
                        value={labelVal ?? ''}
                        onChange={(label) => {
                          clearErrors('label');
                          labelChange(label);
                        }}
                        error={invalid}
                      />
                    </FormGroup>
                  )}
                />
              )}
            </>
          )}
        />

        <div css={ToggleRow}>
          <FormGroup
            label="표시 여부"
            errorText={formState.errors.isVisibleDefault?.message}
            cssStyle={ToggleGroupStyle}
          >
            <Controller
              control={control}
              name="isVisibleDefault"
              render={({ field: { value, onChange } }) => (
                <ToggleButton
                  checked={value}
                  onChange={(checked) => {
                    clearErrors('isVisibleDefault');
                    onChange(checked);
                  }}
                >
                  {value ? '표시' : '미표시'}
                </ToggleButton>
              )}
            />
          </FormGroup>
          <FormGroup label="잠금 여부" cssStyle={ToggleGroupStyle}>
            <Controller
              control={control}
              name="isLockedDefault"
              render={({ field: { value, onChange } }) => (
                <ToggleButton checked={value} onChange={onChange}>
                  {value ? '잠금' : '미잠금'}
                </ToggleButton>
              )}
            />
          </FormGroup>
        </div>

        <div css={ActionRow}>
          <Button type="plainDestructive" onClick={() => handleDeleteQuest()}>
            퀘스트 삭제
          </Button>
          <Button htmlType="submit" loading={isSubmitting} disabled={!formState.isDirty}>
            정보 저장
          </Button>
        </div>
      </form>

      {/* Action Handler - wrapped in MemoryRouter for useParams compatibility */}
      <div css={ActionHandlerSection}>
        <Typography as="h4" type="button" cssStyle={SectionTitle}>
          액션 핸들러
        </Typography>
        <MemoryRouter initialEntries={[fakePath]}>
          <Route path="/apps/:appId/projects/:projectId/scenarios/:scenarioId/missions/:missionId/quests/:questId">
            <ActionHandler key={questId} availabelType="quest" isEmbedded />
          </Route>
        </MemoryRouter>
      </div>
    </div>
  );
}

const Layout = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.large};
`;

const FormSection = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.medium};
`;

const SectionTitle = css`
  margin-bottom: ${spacing.common.xsmall};
`;

const ToggleRow = css`
  display: flex;
  gap: ${spacing.common.medium};
`;

const ToggleGroupStyle = css`
  flex: 1;
`;

const ActionRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: ${spacing.common.small};
`;

const ActionHandlerSection = css`
  border-top: 1px solid ${color.border_default_subdued};
  padding-top: ${spacing.common.large};
`;
